import React, {
  useRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useEffect,
  useReducer,
  useId,
  useState,
} from 'react';
import type {
  ChatWidgetProps,
  ChatWidgetAPI,
  Attachment,
  ProductAction,
  ProductCard,
  OutcomeTrackingConfig,
  Message,
} from './types';
import { RECORD_VIEW_CONFIG, DEFAULT_RECORD_DATA } from './constants';
import { STORAGE_KEY } from './constants/storage';
import { exportConversation } from './utils/export';
import { validateFiles } from './utils/fileValidation';
import { isHostAllowed, isOriginAllowed } from './utils/domain';
import { isWithinAvailability } from './utils/availability';
import { isSafeUrl } from './utils/urlSafety';
import { isReorderLikeAction } from './utils/actionFilters';
import { sanitizeMessagesForStorage } from './utils/messageStorage';
import { useSessionHistory } from './hooks/useSessionHistory';
import { useAutoOpen } from './hooks/useAutoOpen';
import { useConsent } from './hooks/useConsent';
import { useAnalytics } from './hooks/useAnalytics';
import { useOpenState } from './hooks/useOpenState';
import { useThread } from './hooks/useThread';
import { useChat } from './hooks/useChat';
import { useTelemetry } from './hooks/useTelemetry';
import { useDragDrop } from './hooks/useDragDrop';
import { useLocalStorage } from './hooks/useLocalStorage';
import { ChatProvider } from './context/ChatContext';
import type { ChatContextValue } from './context/ChatContext';
import ErrorBoundary from './components/ErrorBoundary';
import MinimizedButton from './components/MinimizedButton';
import Header from './components/Header/Header';
import MainContent from './components/MainContent';
import InputArea from './components/InputArea/InputArea';
import PreChatForm from './components/PreChatForm';
import SessionPanel from './components/SessionPanel';
import TelemetryPanel from './components/TelemetryPanel';
import { getSessionId } from './utils/session';
import { useResolvedProps } from './hooks/useResolvedProps';
import { useScrollManagement } from './hooks/useScrollManagement';
import { useBusinessActions } from './hooks/useBusinessActions';
import { useKeyboardNavigation } from './hooks/useKeyboardNavigation';
import { useIntroMessage } from './hooks/useIntroMessage';
import { useRemoteSettings } from './hooks/useRemoteSettings';
import { useOutcomeTracking } from './hooks/useOutcomeTracking';
import { useIdleTimeout } from './hooks/useIdleTimeout';
import { loadHighlighter } from './utils/markdown';
import { uiReducer, initialUIState } from './reducers/uiReducer';
import { resolveWidgetStrings, resolveTextDirection, formatWidgetString } from './utils/i18n';

const SETTINGS_CONSOLE_URL_TEMPLATES = /\{customerId\}|\{id\}|:customerId|:id/;
const DEFAULT_SETTINGS_CONSOLE_URL_TEMPLATE = '/customers/{customerId}/chat-widget-settings';

// ---- ChatWidgetInner ----
const ChatWidgetInner = React.forwardRef<ChatWidgetAPI, ChatWidgetProps>(
  function ChatWidgetInner(props, ref) {
    const { remoteSettings, isMobile } = useRemoteSettings({
      settingsEndpoint: props.settingsEndpoint || null,
      settingsApiKey: props.settingsApiKey ?? props.apiKey ?? null,
      onError: props.onError,
    });

    const resolved = useResolvedProps(props, remoteSettings);
    const {
      readSetting,
      companyName,
      companyLogo,
      agentName,
      agentAvatar,
      theme,
      darkMode,
      welcomeMessage,
      suggestedActions,
      placeholder,
      language,
      textDirection,
      translations,
      customerId,
      settingsConsoleUrl: settingsConsoleUrlTemplate,
      settingsConsoleLabel,
      agentOrgId,
      agentId,
      kbCollection,
      kbApiKey,
      kbTopK,
      kbScoreThreshold,
      conversionPrompt,
      sessionHistoryEnabled,
      maxSessions,
      allowAttachments,
      allowVoice,
      allowVideo,
      apiKey,
      widgetAuthToken,
      apiBaseUrl,
      messageEndpoint,
      messageCreateEndpoint,
      streamEndpoint,
      threadEndpoint,
      threadCreateEndpoint,
      threadUpdateEndpoint,
      threadDeleteEndpoint,
      cartEndpoint,
      analyticsBaseUrl,
      analyticsEndpoint,
      analyticsSampleRate,
      analyticsEnabled,
      analyticsApiKey,
      telemetryEnabled,
      telemetryPollIntervalMs,
      telemetryShowContext,
      telemetryEndpoint,
      provider,
      enableStreaming,
      enableToolVisualization,
      enableRichMessages,
      enableSourceCitations,
      enableThinking,
      defaultOpen,
      subjectProp,
      channel,
      threadIdProp,
      threadIdControlled,
      threadPollIntervalMs,
      department,
      customFields,
      records,
      effectivePersistThread,
      position,
      zIndex,
      showViewTabs,
      showTimestamps,
      filteredBubbleOptions,
      maxInputLength,
      inputWarningThreshold,
      maxMessagesPerSession,
      rateLimit,
      maxFileSizeMB,
      maxFileCount,
      allowedFileTypes,
      allowedDomains,
      allowedOrigins,
      availability,
      offlineMessage,
      sessionConfig,
      autoOpenRules,
      autoOpenDelayMs,
      preChatForm,
      consent,
      doNotTrain,
      retentionDays,
      sessionVariables,
      retryPolicy,
      lazyLoad,
      customCss,
      cspNonce,
      idleTimeout: idleTimeoutConfig,
    } = resolved;

    const open = props.open;
    const onOpenChange = props.onOpenChange;
    const onSend = props.onSend;
    const onReceive = props.onReceive;
    const onError = props.onError;
    const onThreadCreate = props.onThreadCreate;
    const onThreadUpdate = props.onThreadUpdate;
    const onThreadDelete = props.onThreadDelete;
    const onMessageCreate = props.onMessageCreate;
    const onHandoffRequest = props.onHandoffRequest;
    const onVoiceStart = props.onVoiceStart;
    const onVideoStart = props.onVideoStart;
    const onProductAction = props.onProductAction;
    const buildMessagePayload = props.buildMessagePayload;
    const parseResponse = props.parseResponse;
    const buildThreadUrl = props.buildThreadUrl;
    const buildThreadRequest = props.buildThreadRequest;
    const buildThreadUpdateUrl = props.buildThreadUpdateUrl;
    const buildThreadDeleteUrl = props.buildThreadDeleteUrl;
    const parseThreadResponse = props.parseThreadResponse;
    const parseThreadCreateResponse = props.parseThreadCreateResponse;

    const [ui, uiDispatch] = useReducer(uiReducer, {
      ...initialUIState,
      settings: { ...initialUIState.settings, language },
    });
    const widgetId = useId();
    const titleId = `${widgetId}-title`;
    const tabsIdBase = `${widgetId}-tabs`;
    const activeTabId = `${tabsIdBase}-tab-${ui.view}`;
    const activePanelId = `${tabsIdBase}-panel-${ui.view}`;
    const dialogId = `${widgetId}-dialog`;
    const widgetStrings = useMemo(
      () => resolveWidgetStrings(language, translations),
      [language, translations]
    );
    const widgetDirection = useMemo(
      () => resolveTextDirection(language, textDirection),
      [language, textDirection]
    );
    const widgetLanguage = useMemo(() => String(language || 'en').trim() || 'en', [language]);

    const subject = subjectProp ?? '';

    const preChatEnabled = useMemo(() => {
      if (typeof preChatForm?.enabled === 'boolean') return preChatForm.enabled;
      return Array.isArray(preChatForm?.fields) && preChatForm.fields.length > 0;
    }, [preChatForm]);
    const [preChatData, setPreChatData] = useState<Record<string, string>>({});
    const [preChatComplete, setPreChatComplete] = useState<boolean>(() => !preChatEnabled);

    useEffect(() => {
      setPreChatComplete(!preChatEnabled);
    }, [preChatEnabled]);

    const consentRequired = useMemo(() => {
      if (typeof consent?.required === 'boolean') return consent.required;
      const flag = readSetting('security.consentRequired');
      return !!flag;
    }, [consent, readSetting]);
    const consentText = consent?.text || widgetStrings.consentDefaultText;
    const consentLinkText = consent?.linkText || widgetStrings.consentLearnMore;
    const safeConsentLinkUrl = isSafeUrl((consent?.linkUrl || '').trim())
      ? (consent?.linkUrl || '').trim()
      : '';
    const consentVersion = consent?.version || 'v1';
    const consentStorageKey = consent?.storageKey || 'rw_chat_consent';
    const consentRemember = consent?.remember !== false;

    const { hasConsent, setHasConsent } = useConsent({
      required: consentRequired,
      version: consentVersion,
      storageKey: consentStorageKey,
      remember: consentRemember,
    });

    const analyticsFields = useMemo(
      () => ({
        ...customFields,
        ...(sessionVariables ? { sessionVariables } : {}),
        ...(Object.keys(preChatData || {}).length > 0 ? { preChat: preChatData } : {}),
      }),
      [customFields, sessionVariables, preChatData]
    );

    // ---- Hooks ----
    const trackAnalytics = useAnalytics({
      analyticsBaseUrl,
      apiKey,
      customFields: analyticsFields,
      analyticsEnabled,
      analyticsSampleRate,
      analyticsEndpoint,
      analyticsApiKey: analyticsApiKey || undefined,
      sessionConfig,
    });

    const [domainAllowed, setDomainAllowed] = useState<boolean>(true);
    const [domainCheckReady, setDomainCheckReady] = useState<boolean>(false);
    useEffect(() => {
      if (typeof window === 'undefined') return;
      const host = window.location.hostname;
      const origin = window.location.origin;
      const allowed =
        isHostAllowed(host, allowedDomains || [], []) &&
        isOriginAllowed(origin, allowedOrigins || []);
      setDomainAllowed(allowed);
      setDomainCheckReady(true);
      if (!allowed) {
        trackAnalytics('domain_blocked', { host, origin });
      }
    }, [allowedDomains, allowedOrigins, trackAnalytics]);

    const [isAvailable, setIsAvailable] = useState<boolean>(() =>
      isWithinAvailability(availability)
    );
    useEffect(() => {
      if (!availability || availability.enabled === false) {
        setIsAvailable(true);
        return;
      }
      const tick = () => setIsAvailable(isWithinAvailability(availability));
      tick();
      const id = setInterval(tick, 60_000);
      return () => clearInterval(id);
    }, [availability]);

    const { minimized, openChat, closeChat } = useOpenState({
      open,
      defaultOpen,
      onOpenChange,
      trackAnalytics,
    });
    const isMobileFullscreen = isMobile && !minimized;

    useEffect(() => {
      uiDispatch({ type: 'SET_FULLSCREEN', isFullscreen: isMobileFullscreen });
    }, [isMobileFullscreen, uiDispatch]);

    const agentMeta = useMemo(() => {
      const meta = {
        orgId: agentOrgId || undefined,
        agentId: agentId || undefined,
        kbCollection: kbCollection || undefined,
        kbApiKey: kbApiKey || undefined,
        kbTopK: typeof kbTopK === 'number' ? kbTopK : undefined,
        kbScoreThreshold: typeof kbScoreThreshold === 'number' ? kbScoreThreshold : undefined,
      };
      const hasValues = Object.values(meta).some(value => value !== undefined && value !== '');
      return hasValues ? meta : null;
    }, [agentOrgId, agentId, kbCollection, kbApiKey, kbTopK, kbScoreThreshold]);

    const metaExtras = useMemo(
      () => ({
        ...(Object.keys(preChatData || {}).length > 0 ? { preChat: preChatData } : {}),
        ...(consentRequired ? { consent: { accepted: hasConsent, version: consentVersion } } : {}),
        ...(doNotTrain !== undefined || retentionDays !== undefined
          ? { policy: { doNotTrain, retentionDays } }
          : {}),
        ...(sessionVariables ? { sessionVariables } : {}),
        ...(conversionPrompt ? { conversionPrompt } : {}),
        ...(agentMeta ? { agent: agentMeta } : {}),
      }),
      [
        preChatData,
        consentRequired,
        hasConsent,
        consentVersion,
        doNotTrain,
        retentionDays,
        sessionVariables,
        conversionPrompt,
        agentMeta,
      ]
    );

    const [threadPollIntervalMsEffective, setThreadPollIntervalMsEffective] =
      useState(threadPollIntervalMs);
    const isThreadControlled = !!threadIdControlled;

    // Use ref to bridge thread → chat setMessages (thread is initialized before chat)
    const chatSetMessagesRef = useRef<(msgs: import('./types').Message[]) => void>(() => {});

    const thread = useThread({
      threadIdProp,
      threadIdControlled: isThreadControlled,
      threadEndpoint,
      messageEndpoint,
      apiBaseUrl,
      apiKey,
      widgetAuthToken: widgetAuthToken || undefined,
      orgId: agentOrgId || undefined,
      agentId: agentId || undefined,
      provider,
      channel,
      threadCreateEndpoint,
      threadUpdateEndpoint,
      threadDeleteEndpoint,
      threadPollIntervalMs: threadPollIntervalMsEffective,
      persistThread: effectivePersistThread,
      lazyLoad,
      minimized,
      subject,
      department,
      customFields,
      metaExtras,
      sessionConfig,
      buildThreadUrl,
      buildThreadRequest,
      buildThreadUpdateUrl,
      buildThreadDeleteUrl,
      parseThreadResponse,
      parseThreadCreateResponse,
      onThreadCreate,
      onThreadUpdate,
      onThreadDelete,
      trackAnalytics,
      setMessages: msgs => {
        chatSetMessagesRef.current(msgs as import('./types').Message[]);
      },
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const telemetryHeaders = useMemo(() => thread.withAuthHeaders(), [thread.withAuthHeaders]);
    const telemetry = useTelemetry({
      enabled: telemetryEnabled,
      endpoint: telemetryEndpoint,
      pollIntervalMs: telemetryPollIntervalMs,
      headers: telemetryHeaders,
      orgId: agentOrgId || undefined,
      agentId: agentId || undefined,
    });

    const outcomeTrackingConfig = useMemo<OutcomeTrackingConfig>(() => {
      const fromSettings = readSetting('outcomes');
      const base =
        fromSettings && typeof fromSettings === 'object' && !Array.isArray(fromSettings)
          ? (fromSettings as Record<string, unknown>)
          : {};
      const override =
        props.outcomes && typeof props.outcomes === 'object' && !Array.isArray(props.outcomes)
          ? (props.outcomes as Record<string, unknown>)
          : {};
      const merged: Record<string, unknown> = { ...base, ...override };

      const cfg: OutcomeTrackingConfig = {};
      if (typeof merged.enabled === 'boolean') cfg.enabled = merged.enabled;
      if (typeof merged.action === 'string') cfg.action = merged.action;
      if (typeof merged.provider === 'string') cfg.provider = merged.provider;
      if (typeof merged.customEventName === 'string') cfg.customEventName = merged.customEventName;
      if (typeof merged.postMessageType === 'string') cfg.postMessageType = merged.postMessageType;
      if (typeof merged.storageKey === 'string') cfg.storageKey = merged.storageKey;
      if (typeof merged.consumeStorageKey === 'boolean')
        cfg.consumeStorageKey = merged.consumeStorageKey;
      if (Array.isArray(merged.allowedMessageOrigins)) {
        cfg.allowedMessageOrigins = merged.allowedMessageOrigins
          .map(v => (typeof v === 'string' ? v : null))
          .filter(Boolean) as string[];
      }
      return cfg;
    }, [readSetting, props.outcomes]);

    const outcomeTracking = useOutcomeTracking({
      config: outcomeTrackingConfig,
      trackAnalytics,
      threadId: thread.threadId,
      onOutcome: props.onOutcome,
      sessionConfig,
    });

    const resolvedSettingsConsoleUrlTemplate = useMemo(() => {
      const explicitTemplate = settingsConsoleUrlTemplate?.trim();
      if (explicitTemplate) return explicitTemplate;

      if (!customerId?.trim()) return '';
      if (!props.settingsEndpoint) return '';

      const endpointValue = String(props.settingsEndpoint).trim();
      if (!endpointValue) return '';

      const hasWindowOrigin = typeof window !== 'undefined' && window.location?.origin;
      try {
        const endpointUrl = hasWindowOrigin
          ? new URL(endpointValue, window.location.origin)
          : new URL(endpointValue);
        return `${endpointUrl.origin}${DEFAULT_SETTINGS_CONSOLE_URL_TEMPLATE}`;
      } catch {
        return '';
      }
    }, [customerId, props.settingsEndpoint, settingsConsoleUrlTemplate]);

    const settingsConsoleUrl = useMemo(() => {
      if (!resolvedSettingsConsoleUrlTemplate) return '';
      const hasPlaceholder = SETTINGS_CONSOLE_URL_TEMPLATES.test(
        resolvedSettingsConsoleUrlTemplate
      );
      const resolvedCustomerId = customerId?.trim();
      if (hasPlaceholder && !resolvedCustomerId) return '';

      if (!hasPlaceholder) return resolvedSettingsConsoleUrlTemplate.trim();

      const encodedCustomerId = encodeURIComponent(resolvedCustomerId || '');
      return resolvedSettingsConsoleUrlTemplate
        .replace(/\{customerId\}/g, encodedCustomerId)
        .replace(/\{id\}/g, encodedCustomerId)
        .replace(/:customerId/g, encodedCustomerId)
        .replace(/:id/g, encodedCustomerId);
    }, [customerId, resolvedSettingsConsoleUrlTemplate]);

    const safeSettingsConsoleUrl = useMemo(
      () => (settingsConsoleUrl && isSafeUrl(settingsConsoleUrl) ? settingsConsoleUrl : ''),
      [settingsConsoleUrl]
    );

    const chat = useChat({
      enableStreaming,
      enableToolVisualization,
      enableRichMessages,
      enableSourceCitations,
      enableThinking,
      streamEndpoint,
      messageEndpoint,
      messageCreateEndpoint,
      apiBaseUrl,
      apiKey,
      provider,
      subject,
      department,
      channel,
      customFields,
      metaExtras,
      threadId: thread.threadId,
      threadBase: thread.threadBase,
      companyName,
      saveHistory: ui.settings.saveHistory,
      loadHistory: !sessionHistoryEnabled,
      historyStorage: sessionConfig?.storage || 'local',
      buildMessagePayload,
      parseResponse,
      onSend,
      onReceive,
      onError,
      onMessageCreate,
      trackAnalytics,
      trackOutcome: outcomeTracking.trackOutcome,
      withAuthHeaders: thread.withAuthHeaders,
      fetchThread: thread.fetchThread,
      createThread: thread.createThread,
      threadCreateEndpoint,
      minimized,
      setThreadId: thread.setThreadId,
      sessionConfig,
      retryPolicy,
    });

    // Wire thread setMessages to chat's dispatcher
    chatSetMessagesRef.current = chat.setMessages;

    const sessionHistoryActive = sessionHistoryEnabled && ui.settings.saveHistory;
    const {
      sessionsOpen,
      setSessionsOpen,
      sessions,
      activeSessionId,
      createNewSession,
      selectSession,
      removeSession,
    } = useSessionHistory({
      active: sessionHistoryActive,
      maxSessions,
      setMessages: chat.setMessages,
      chatDispatch: chat.dispatch,
      setThreadId: thread.setThreadId,
      uiDispatch,
      messages: chat.state.messages,
      threadId: thread.threadId ?? null,
      sessionConfig,
    });

    useEffect(() => {
      const nextIntervalMs =
        chat.state.isEscalated && (!threadPollIntervalMs || threadPollIntervalMs <= 0)
          ? 3000
          : threadPollIntervalMs;
      if (nextIntervalMs !== threadPollIntervalMsEffective) {
        setThreadPollIntervalMsEffective(nextIntervalMs);
      }
    }, [chat.state.isEscalated, threadPollIntervalMs, threadPollIntervalMsEffective]);

    const escalationMonitorRef = useRef({ isActive: false, anchorMessageCount: 0 });
    const escalationFallbackTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const latestChatStateRef = useRef<{
      isEscalated: boolean;
      messages: Message[];
      streamingMessage: Message | null;
    }>({
      isEscalated: false,
      messages: [],
      streamingMessage: null,
    });
    latestChatStateRef.current = {
      isEscalated: chat.state.isEscalated,
      messages: chat.state.messages,
      streamingMessage: chat.state.streamingMessage,
    };

    const clearEscalationFallback = useCallback(() => {
      if (escalationFallbackTimeoutRef.current) {
        clearTimeout(escalationFallbackTimeoutRef.current);
        escalationFallbackTimeoutRef.current = null;
      }
    }, []);

    const normalizeEscalationSource = useCallback((value: unknown): string => {
      return String(value || '')
        .trim()
        .toLowerCase()
        .replace(/[\s_-]+/g, ' ')
        .replace(/[^a-z0-9 ]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    }, []);

    const isLikelyLiveAgentMessage = useCallback(
      (msg: (Message & { threadSource?: string; threadRole?: string }) | null) => {
        if (!msg) return false;
        const source = normalizeEscalationSource(msg.threadSource);
        const role = normalizeEscalationSource(msg.threadRole);
        const normalizedSource = source || role;
        if (!normalizedSource) return false;
        const hasHumanSignal =
          /\b(agent|human|support|operator|representative|advisor|adviser|cs|csr|customer service|live agent|gorgias)\b/.test(
            normalizedSource
          );
        const hasAutomatedSignal =
          /\b(ai|assistant|bot|llm|gpt|claude|system|widget|noelle)\b/.test(normalizedSource);
        if (hasHumanSignal) return true;
        if (hasAutomatedSignal) return false;
        return false;
      },
      [normalizeEscalationSource]
    );

    const hasEscalationResponse = useCallback(() => {
      const state = latestChatStateRef.current;
      const anchor = escalationMonitorRef.current.anchorMessageCount;
      const hasStreamingReply =
        (state.streamingMessage?.sender as string) !== 'user' &&
        !!state.streamingMessage &&
        isLikelyLiveAgentMessage(state.streamingMessage);
      if (state.messages.length <= anchor && !hasStreamingReply) {
        return false;
      }
      return (
        state.messages
          .slice(anchor)
          .some(msg => (msg.sender as string) !== 'user' && isLikelyLiveAgentMessage(msg)) ||
        ((state.streamingMessage?.sender as string) !== 'user' &&
          isLikelyLiveAgentMessage(state.streamingMessage))
      );
    }, [isLikelyLiveAgentMessage]);

    const addEscalationFallbackMessage = useCallback(() => {
      const now = new Date();
      chat.dispatch({
        type: 'ADD_MESSAGE',
        message: {
          id: `handoff-fallback-${now.getTime()}`,
          sender: 'ai',
          text: 'Noelle (AI) comes back and says all of our agents are assisting other customers, can you leave us a message and we will have someone on our team follow up with you.',
          timestamp: now.toLocaleTimeString(),
          status: 'delivered',
        },
      });
      trackAnalytics('handoff_followup_fallback', { delayMs: 60000 });
      escalationMonitorRef.current.isActive = false;
      escalationFallbackTimeoutRef.current = null;
    }, [chat, trackAnalytics]);

    useEffect(() => {
      if (!chat.state.isEscalated) {
        escalationMonitorRef.current = { isActive: false, anchorMessageCount: 0 };
        clearEscalationFallback();
        return;
      }
      escalationMonitorRef.current = {
        isActive: true,
        anchorMessageCount: chat.state.messages.length,
      };
      clearEscalationFallback();
      escalationFallbackTimeoutRef.current = setTimeout(() => {
        if (!latestChatStateRef.current.isEscalated) return;
        if (!escalationMonitorRef.current.isActive) return;
        if (hasEscalationResponse()) return;
        addEscalationFallbackMessage();
      }, 60_000);
      return clearEscalationFallback;
    }, [
      chat.state.isEscalated,
      hasEscalationResponse,
      addEscalationFallbackMessage,
      clearEscalationFallback,
    ]);

    useEffect(() => {
      if (!chat.state.isEscalated || !escalationMonitorRef.current.isActive) return;
      if (!hasEscalationResponse()) return;
      escalationMonitorRef.current.isActive = false;
      clearEscalationFallback();
    }, [
      chat.state.isEscalated,
      chat.state.messages,
      chat.state.streamingMessage,
      hasEscalationResponse,
      clearEscalationFallback,
    ]);

    const sessionsPanelRef = useRef<HTMLDivElement>(null);
    const sessionsPreviousFocusRef = useRef<HTMLElement | null>(null);
    const sessionsFocusRestoreTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const inputFocusTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const openSessions = useCallback(() => {
      try {
        sessionsPreviousFocusRef.current = document.activeElement as HTMLElement | null;
      } catch {
        sessionsPreviousFocusRef.current = null;
      }
      setSessionsOpen(true);
    }, [setSessionsOpen]);

    const closeSessions = useCallback(() => {
      setSessionsOpen(false);
      // Restore focus to whatever opened the sessions panel, if possible.
      if (sessionsFocusRestoreTimeoutRef.current) {
        clearTimeout(sessionsFocusRestoreTimeoutRef.current);
      }
      sessionsFocusRestoreTimeoutRef.current = setTimeout(() => {
        sessionsPreviousFocusRef.current?.focus();
      }, 0);
    }, [setSessionsOpen]);

    const messagesForStorage = useMemo(
      () => sanitizeMessagesForStorage(chat.state.messages),
      [chat.state.messages]
    );

    // Debounced storage persistence (uses sessionStorage when configured)
    const historyStorageKind = sessionConfig?.storage || 'local';
    useLocalStorage(
      STORAGE_KEY,
      messagesForStorage,
      ui.settings.saveHistory && !sessionHistoryEnabled,
      300,
      historyStorageKind
    );

    const {
      messagesEndRef,
      messagesContainerRef,
      scrollToBottomVisible,
      newMessagesWhileScrolledUp,
      handleMessagesScroll,
      scrollToBottom,
    } = useScrollManagement({
      messages: chat.state.messages,
      streamingMessage: chat.state.streamingMessage,
      activeSessionId,
      minimized,
    });

    const chatContainerRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const textAreaRef = useRef<HTMLTextAreaElement>(null);
    const rateLimitRef = useRef<number[]>([]);
    const attachmentsRef = useRef(ui.attachments);

    useEffect(() => {
      attachmentsRef.current = ui.attachments;
    }, [ui.attachments]);

    // Welcome/intro message
    useIntroMessage({
      historyLoaded: chat.historyLoaded,
      messagesLength: chat.state.messages.length,
      welcomeMessage: welcomeMessage || '',
      filteredBubbleOptions,
      activeSessionId,
      threadId: thread.threadId,
      dispatch: chat.dispatch,
    });

    // Focus composer when chat opens
    useEffect(() => {
      if (!minimized) {
        if (inputFocusTimeoutRef.current) {
          clearTimeout(inputFocusTimeoutRef.current);
        }
        inputFocusTimeoutRef.current = setTimeout(() => {
          try {
            textAreaRef.current?.focus();
          } catch {
            /* */
          }
        }, 50);
      }
    }, [minimized]);

    useEffect(() => {
      return () => {
        if (sessionsFocusRestoreTimeoutRef.current) {
          clearTimeout(sessionsFocusRestoreTimeoutRef.current);
        }
        if (inputFocusTimeoutRef.current) {
          clearTimeout(inputFocusTimeoutRef.current);
        }
      };
    }, []);

    const widgetLoadedTrackedRef = useRef(false);

    // Track widget load & preload highlighter
    useEffect(() => {
      if (widgetLoadedTrackedRef.current) return;
      widgetLoadedTrackedRef.current = true;
      trackAnalytics('widget_loaded', {});
      loadHighlighter();
    }, [trackAnalytics]);

    const autoOpenConfig = useMemo(() => {
      const hasAnyRule =
        typeof autoOpenDelayMs === 'number' ||
        typeof autoOpenRules?.delayMs === 'number' ||
        typeof autoOpenRules?.scrollPercent === 'number' ||
        !!autoOpenRules?.exitIntent ||
        typeof autoOpenRules?.idleMs === 'number';
      return {
        enabled: typeof autoOpenRules?.enabled === 'boolean' ? autoOpenRules.enabled : hasAnyRule,
        delayMs:
          typeof autoOpenRules?.delayMs === 'number' ? autoOpenRules.delayMs : autoOpenDelayMs,
        scrollPercent: autoOpenRules?.scrollPercent,
        exitIntent: autoOpenRules?.exitIntent,
        idleMs: autoOpenRules?.idleMs,
        maxOpensPerSession: autoOpenRules?.maxOpensPerSession ?? 1,
      };
    }, [autoOpenRules, autoOpenDelayMs]);

    // Auto-open rules
    useAutoOpen({
      controlledOpen: open,
      config: autoOpenConfig,
      minimized,
      domainAllowed: domainCheckReady && domainAllowed,
      openChat,
      trackAnalytics,
    });

    // Keyboard navigation & focus trap
    useKeyboardNavigation({
      minimized,
      sessionsOpen,
      closeSessions,
      closeChat,
      chatContainerRef,
      sessionsPanelRef,
    });

    // Revoke object URLs on unmount
    useEffect(() => {
      return () => {
        attachmentsRef.current.forEach(a => {
          if (a.previewUrl) URL.revokeObjectURL(a.previewUrl);
        });
      };
    }, []);

    // ---- Derived data ----
    const mergedRecords = useMemo(
      () => ({
        orders: records?.orders ?? DEFAULT_RECORD_DATA.orders,
        products: records?.products ?? DEFAULT_RECORD_DATA.products,
        subscriptions: records?.subscriptions ?? DEFAULT_RECORD_DATA.subscriptions,
        returns: records?.returns ?? DEFAULT_RECORD_DATA.returns,
        exchanges: records?.exchanges ?? DEFAULT_RECORD_DATA.exchanges,
      }),
      [records]
    );

    const viewCounts = useMemo(
      () => ({
        chat: chat.state.messages.length,
        orders: mergedRecords.orders?.length || 0,
        products: mergedRecords.products?.length || 0,
        subscriptions: mergedRecords.subscriptions?.length || 0,
        returns: mergedRecords.returns?.length || 0,
        exchanges: mergedRecords.exchanges?.length || 0,
      }),
      [chat.state.messages, mergedRecords]
    );

    const currentViewMode = useMemo(() => {
      const config = RECORD_VIEW_CONFIG[ui.view];
      if (!config) return undefined;
      return ui.viewModes[ui.view] ?? config.defaultMode ?? config?.modes?.[0]?.key ?? 'overview';
    }, [ui.view, ui.viewModes]);

    const showPreChat = preChatEnabled && !preChatComplete && domainAllowed;
    const needsConsent = consentRequired && !hasConsent;
    const isOffline = availability?.enabled && !isAvailable;
    const blockWhenOffline = !!availability?.blockWhenOffline;
    const userMessageCount = useMemo(
      () => chat.state.messages.filter(m => m.sender === 'user').length,
      [chat.state.messages]
    );
    const messageLimitReached =
      typeof maxMessagesPerSession === 'number' && maxMessagesPerSession > 0
        ? userMessageCount >= maxMessagesPerSession
        : false;
    const inputDisabled =
      !domainAllowed ||
      showPreChat ||
      needsConsent ||
      (isOffline && blockWhenOffline) ||
      messageLimitReached;
    const statusMessage = useMemo(() => {
      if (!domainAllowed) return widgetStrings.domainBlockedStatus;
      if (messageLimitReached) return widgetStrings.messageLimitStatus;
      if (isOffline && blockWhenOffline && !offlineMessage) return widgetStrings.offlineStatus;
      return '';
    }, [
      domainAllowed,
      messageLimitReached,
      isOffline,
      blockWhenOffline,
      offlineMessage,
      widgetStrings.domainBlockedStatus,
      widgetStrings.messageLimitStatus,
      widgetStrings.offlineStatus,
    ]);

    const fileValidationOptions = useMemo(
      () => ({
        allowedFileTypes,
        maxFileSize: typeof maxFileSizeMB === 'number' ? maxFileSizeMB * 1024 * 1024 : undefined,
        maxFileCount,
      }),
      [allowedFileTypes, maxFileSizeMB, maxFileCount]
    );

    // ---- Handlers ----
    const handleViewModeChange = useCallback(
      (viewKey: string, modeKey: string) => {
        uiDispatch({ type: 'SET_VIEW_MODE', viewKey, modeKey });
        trackAnalytics('view_mode_change', { view: viewKey, mode: modeKey });
      },
      [trackAnalytics]
    );

    const handleExport = useCallback(
      (format: string) => {
        exportConversation(chat.state.messages, format);
        trackAnalytics('conversation_exported', {
          format,
          messageCount: chat.state.messages.length,
        });
      },
      [chat.state.messages, trackAnalytics]
    );

    const handleFileUpload = useCallback(
      (fileList: FileList) => {
        if (!allowAttachments || inputDisabled) return;
        const files = Array.from(fileList || []);
        const { valid, errors } = validateFiles(
          files,
          ui.attachments.length,
          fileValidationOptions
        );
        if (errors.length) {
          console.warn(
            'File validation errors:',
            errors.map(e => e.reason)
          );
          uiDispatch({ type: 'SET_ATTACHMENT_ERRORS', errors: errors.map(e => e.reason) });
          trackAnalytics('file_validation_error', { count: errors.length });
        } else {
          uiDispatch({ type: 'CLEAR_ATTACHMENT_ERRORS' });
        }
        const mapped: Attachment[] = valid.map((file, idx) => ({
          id: `${Date.now()}-${idx}`,
          name: file.name,
          size: file.size,
          type: file.type,
          file,
          previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
        }));
        uiDispatch({ type: 'ADD_ATTACHMENTS', attachments: mapped });
      },
      [
        allowAttachments,
        inputDisabled,
        ui.attachments.length,
        trackAnalytics,
        fileValidationOptions,
      ]
    );

    const handleSend = useCallback(async () => {
      if (!domainAllowed) {
        chat.dispatch({ type: 'SEND_ERROR', error: widgetStrings.domainBlockedStatus });
        return;
      }
      if (showPreChat) {
        chat.dispatch({
          type: 'SEND_ERROR',
          error: widgetStrings.preChatRequiredError,
        });
        return;
      }
      if (needsConsent) {
        chat.dispatch({ type: 'SEND_ERROR', error: widgetStrings.consentRequiredError });
        return;
      }
      if (isOffline && blockWhenOffline) {
        chat.dispatch({
          type: 'SEND_ERROR',
          error: offlineMessage || widgetStrings.offlineStatus,
        });
        return;
      }
      if (messageLimitReached) {
        chat.dispatch({
          type: 'SEND_ERROR',
          error: widgetStrings.messageLimitStatus,
        });
        return;
      }

      if (rateLimit?.maxMessages && rateLimit?.windowMs) {
        const now = Date.now();
        const windowMs = rateLimit.windowMs;
        const maxMessages = rateLimit.maxMessages;
        rateLimitRef.current = rateLimitRef.current.filter(ts => now - ts < windowMs);
        if (rateLimitRef.current.length >= maxMessages) {
          const retryInMs = windowMs - (now - rateLimitRef.current[0]);
          const retryInSec = Math.ceil(retryInMs / 1000);
          chat.dispatch({
            type: 'SEND_ERROR',
            error: formatWidgetString(widgetStrings.rateLimitedError, { seconds: retryInSec }),
          });
          trackAnalytics('rate_limited', { retryInMs, maxMessages, windowMs });
          return;
        }
        rateLimitRef.current.push(now);
      }

      const atts = ui.attachments;
      uiDispatch({ type: 'CLEAR_ATTACHMENT_ERRORS' });
      const sendResult = await chat.handleSend(atts);
      if (sendResult.ok && !sendResult.aborted) uiDispatch({ type: 'CLEAR_ATTACHMENTS' });
      if (sendResult.ok && minimized) uiDispatch({ type: 'INCREMENT_UNREAD' });
    }, [
      domainAllowed,
      showPreChat,
      needsConsent,
      isOffline,
      blockWhenOffline,
      offlineMessage,
      messageLimitReached,
      rateLimit,
      chat,
      ui.attachments,
      minimized,
      trackAnalytics,
      widgetStrings,
    ]);

    const { handleButtonClick, handleFormSubmit } = useBusinessActions({
      chatDispatch: chat.dispatch,
      chatLoading: chat.state.loading,
      createMessage: chat.createMessage,
      threadId: thread.threadId ?? null,
      createThread: thread.createThread,
      threadCreateEndpoint,
      subject,
      companyName,
      channel,
      allowAttachments,
      inputDisabled,
      fileInputRef,
      onHandoffRequest,
      preChatData,
      trackAnalytics,
      onError,
    });

    const handleOrderAction = useCallback(
      ({
        action,
        orderId,
      }: {
        action: {
          label: string;
          value?: string;
          href?: string;
          target?: '_self' | '_blank';
        };
        orderId?: string | null;
      }) => {
        const isReorderAction = isReorderLikeAction(action.label, action.value || '');
        const actionUrl = action.href?.trim();
        const hasActionValue = Boolean((action.value || '').trim());
        const safeUrl = actionUrl && isSafeUrl(actionUrl) ? actionUrl : '';
        const actionRoute = isReorderAction ? 'pdp' : 'default';
        trackAnalytics('order_action', {
          label: action.label,
          value: action.value || null,
          orderId: orderId || null,
          route: actionRoute,
          hasHref: Boolean(safeUrl),
        });
        if (safeUrl) {
          const target = action.target || '_blank';
          if (target === '_self') {
            try {
              window.open(safeUrl, '_self');
            } catch {
              try {
                window.location.assign(safeUrl);
              } catch {
                // Ignore if navigation is blocked.
              }
            }
          } else {
            window.open(safeUrl, target, 'noopener,noreferrer');
          }
          return;
        }
        if (hasActionValue) {
          handleButtonClick(action.value || '');
        }
      },
      [handleButtonClick, trackAnalytics]
    );

    const idleTimeoutClearRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const { resetActivity: resetIdleTimer } = useIdleTimeout({
      enabled: !!idleTimeoutConfig?.enabled && chat.state.messages.length > 0,
      idleMs: idleTimeoutConfig?.idleMs,
      closeMs: idleTimeoutConfig?.closeMs,
      hasMessages: chat.state.messages.length > 0,
      onIdle: useCallback(() => {
        const now = new Date();
        chat.dispatch({
          type: 'ADD_MESSAGE',
          message: {
            id: `idle-${now.getTime()}`,
            sender: 'ai',
            text: 'Is there anything else I can help you with today?',
            timestamp: now.toLocaleTimeString(),
            status: 'delivered',
          },
        });
      }, [chat]),
      onTimeout: useCallback(() => {
        const now = new Date();
        chat.dispatch({
          type: 'ADD_MESSAGE',
          message: {
            id: `timeout-${now.getTime()}`,
            sender: 'ai',
            text: 'This chat session has ended. Feel free to start a new conversation anytime!',
            timestamp: now.toLocaleTimeString(),
            status: 'delivered',
          },
        });
        // Clear session after a brief delay so the user sees the goodbye message
        if (idleTimeoutClearRef.current) {
          clearTimeout(idleTimeoutClearRef.current);
        }
        idleTimeoutClearRef.current = setTimeout(() => {
          chat.setMessages([]);
          if (thread.setThreadId) thread.setThreadId(null);
        }, 3000);
      }, [chat, thread]),
    });

    useEffect(() => {
      return () => {
        if (idleTimeoutClearRef.current) {
          clearTimeout(idleTimeoutClearRef.current);
        }
      };
    }, []);

    // Reset idle timer on every user send
    const originalHandleSendRef = useRef(handleSend);
    originalHandleSendRef.current = handleSend;
    useEffect(() => {
      if (!idleTimeoutConfig?.enabled) return;
      const handler = () => resetIdleTimer();
      // Listen for user activity to reset the timer
      window.addEventListener('rw:user-send', handler);
      return () => window.removeEventListener('rw:user-send', handler);
    }, [idleTimeoutConfig?.enabled, resetIdleTimer]);

    // Reset idle timer whenever messages change (user sent something)
    const prevMsgCountRef = useRef(chat.state.messages.length);
    useEffect(() => {
      if (chat.state.messages.length > prevMsgCountRef.current) {
        const lastMsg = chat.state.messages[chat.state.messages.length - 1];
        if (lastMsg?.sender === 'user') {
          resetIdleTimer();
        }
      }
      prevMsgCountRef.current = chat.state.messages.length;
    }, [chat.state.messages, resetIdleTimer]);

    const handleConsentToggle = useCallback(
      (checked: boolean) => {
        setHasConsent(checked);
        trackAnalytics(checked ? 'consent_accepted' : 'consent_revoked', {
          version: consentVersion,
        });
        if (checked) chat.dispatch({ type: 'CLEAR_ERROR' });
      },
      [setHasConsent, trackAnalytics, consentVersion, chat]
    );

    const handlePreChatSubmit = useCallback(
      (data: Record<string, string>) => {
        setPreChatData(data);
        setPreChatComplete(true);
        trackAnalytics('prechat_submitted', { fields: Object.keys(data) });
        chat.dispatch({ type: 'CLEAR_ERROR' });
      },
      [trackAnalytics, chat]
    );

    const handleVoiceClick = useCallback(() => {
      trackAnalytics('voice_click', {});
      onVoiceStart?.();
    }, [trackAnalytics, onVoiceStart]);

    const handleVideoClick = useCallback(() => {
      trackAnalytics('video_click', {});
      onVideoStart?.();
    }, [trackAnalytics, onVideoStart]);

    const handleProductAction = useCallback(
      ({ action, product }: { action: ProductAction; product: ProductCard }) => {
        const isReorderAction = isReorderLikeAction(action.label, action.value || '');
        const productUrl = action.url || product.url;
        trackAnalytics('product_action', {
          action: action.type,
          label: action.label,
          productId: product.id,
          route: isReorderAction ? 'pdp' : 'default',
          actionHasUrl: Boolean(productUrl),
        });
        const shouldOpenPdp =
          isReorderAction && productUrl && isSafeUrl(productUrl) && typeof window !== 'undefined';
        if (shouldOpenPdp) {
          if (onProductAction) {
            onProductAction({ action, product });
          }
          window.open(productUrl, '_blank', 'noopener,noreferrer');
          return;
        }
        if (onProductAction) {
          onProductAction({ action, product });
          return;
        }
        if (action.type === 'add_to_cart' && typeof window !== 'undefined') {
          const endpoint =
            cartEndpoint ||
            (apiBaseUrl ? `${apiBaseUrl.replace(/\/$/, '')}/cart/add` : '/api/cart/add');
          const variantId = action.value || product.id;
          if (variantId) {
            fetch(endpoint, {
              method: 'POST',
              headers: thread.withAuthHeaders(),
              body: JSON.stringify({
                variantId,
                quantity: 1,
                sessionId: getSessionId(sessionConfig),
                threadId: thread.threadId,
              }),
            })
              .then(res => res.json())
              .then(payload => {
                if (payload?.checkoutUrl && isSafeUrl(payload.checkoutUrl)) {
                  window.open(payload.checkoutUrl, '_blank', 'noopener,noreferrer');
                }
              })
              .catch(err => {
                trackAnalytics('cart_error', { productId: product.id, error: String(err) });
              });
            return;
          }
        }
        if (action.url && isSafeUrl(action.url) && typeof window !== 'undefined') {
          window.open(action.url, '_blank', 'noopener,noreferrer');
        }
      },
      [trackAnalytics, onProductAction, cartEndpoint, apiBaseUrl, thread, sessionConfig]
    );

    // Drag and drop
    const { handleDragEnter, handleDragLeave, handleDragOver, handleDrop, isDraggingFiles } =
      useDragDrop({
        containerRef: chatContainerRef,
        onDrop: handleFileUpload,
        enabled: allowAttachments && !inputDisabled,
      });

    // ---- Imperative handle ----
    useImperativeHandle(ref, () => ({
      createThread: thread.createThread,
      updateThread: thread.updateThread,
      deleteThread: thread.deleteThread,
      createMessage: chat.createMessage,
      fetchThread: thread.fetchThread,
      getSessionId: () => getSessionId(sessionConfig),
      getThreadId: () => thread.threadId,
      trackOutcome: outcomeTracking.trackOutcome,
    }));

    // Cap rendered messages to last 100
    const displayMessages = useMemo(() => chat.state.messages.slice(-100), [chat.state.messages]);

    // Context value to eliminate prop drilling
    const chatContextValue: ChatContextValue = useMemo(
      () => ({
        messages: displayMessages,
        input: chat.state.input,
        loading: chat.state.loading,
        error: chat.state.error,
        isTyping: chat.state.isTyping,
        streamingMessage: chat.state.streamingMessage,
        activeToolCalls: chat.state.activeToolCalls,
        attachments: ui.attachments,
        view: ui.view,
        theme,
        enableToolVisualization,
        enableSourceCitations,
        enableThinking,
        showTimestamps,
        trackAnalytics,
      }),
      [
        displayMessages,
        chat.state.input,
        chat.state.loading,
        chat.state.error,
        chat.state.isTyping,
        chat.state.streamingMessage,
        chat.state.activeToolCalls,
        ui.attachments,
        ui.view,
        theme,
        enableToolVisualization,
        enableSourceCitations,
        enableThinking,
        showTimestamps,
        trackAnalytics,
      ]
    );

    const widgetPositionStyle = useMemo((): React.CSSProperties => {
      const style: React.CSSProperties = { position: 'fixed' };
      if (ui.isFullscreen) {
        style.top = 0;
        style.right = 0;
        style.bottom = 0;
        style.left = 0;
        return style;
      }
      switch (position) {
        case 'bottom-left':
          style.bottom = '1rem';
          style.left = '1rem';
          break;
        case 'top-left':
          style.top = '1rem';
          style.left = '1rem';
          break;
        case 'top-right':
          style.top = '1rem';
          style.right = '1rem';
          break;
        case 'bottom-right':
        default:
          style.bottom = '1rem';
          style.right = '1rem';
          break;
      }
      return style;
    }, [position, ui.isFullscreen]);

    // ---- Render ----
    return (
      <ChatProvider value={chatContextValue}>
        <div
          className="rw-widget"
          style={{ zIndex, overflowX: 'hidden', ...widgetPositionStyle }}
          data-rw-theme={darkMode}
          data-rw-mobile={isMobileFullscreen ? 'true' : 'false'}
          data-rw-dir={widgetDirection}
          lang={widgetLanguage}
          dir={widgetDirection}
        >
          {customCss ? (
            <style nonce={cspNonce || undefined} dangerouslySetInnerHTML={{ __html: customCss }} />
          ) : null}
          {minimized ? (
            <MinimizedButton
              unreadCount={ui.unreadCount}
              theme={theme}
              onClick={openChat}
              controlsId={dialogId}
              strings={widgetStrings}
            />
          ) : (
            <div
              className={`flex flex-col rw-panel ${
                ui.isFullscreen ? 'w-full h-full' : 'w-96 h-[600px]'
              } bg-white rounded-2xl animate-slideUp overflow-x-hidden relative`}
              ref={chatContainerRef}
              id={dialogId}
              role="dialog"
              aria-modal="true"
              aria-label={formatWidgetString(widgetStrings.dialogAriaLabel, {
                company: companyName,
              })}
              aria-labelledby={titleId}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              {allowAttachments && isDraggingFiles && (
                <div className="rw-drag-overlay" role="status" aria-live="polite">
                  <div className="rw-drag-overlay-inner">{widgetStrings.dropFilesToUpload}</div>
                </div>
              )}
              <Header
                view={ui.view}
                viewCounts={viewCounts}
                theme={theme}
                companyName={companyName}
                companyLogo={companyLogo}
                agentName={agentName}
                agentAvatar={agentAvatar}
                titleId={titleId}
                tabsIdBase={tabsIdBase}
                showViewTabs={showViewTabs}
                showSessionsButton={sessionHistoryActive}
                onSessionsClick={openSessions}
                onViewChange={(v: string) => uiDispatch({ type: 'SET_VIEW', view: v })}
                onMinimize={closeChat}
                onExport={handleExport}
                settingsConsoleUrl={safeSettingsConsoleUrl}
                settingsConsoleLabel={settingsConsoleLabel}
                strings={widgetStrings}
              />

              {sessionHistoryActive && sessionsOpen && (
                <SessionPanel
                  sessions={sessions}
                  activeSessionId={activeSessionId}
                  containerRef={sessionsPanelRef}
                  onClose={closeSessions}
                  onCreate={() => {
                    createNewSession();
                    if (inputFocusTimeoutRef.current) {
                      clearTimeout(inputFocusTimeoutRef.current);
                    }
                    inputFocusTimeoutRef.current = setTimeout(
                      () => textAreaRef.current?.focus(),
                      0
                    );
                  }}
                  onSelect={id => {
                    selectSession(id);
                    if (inputFocusTimeoutRef.current) {
                      clearTimeout(inputFocusTimeoutRef.current);
                    }
                    inputFocusTimeoutRef.current = setTimeout(
                      () => textAreaRef.current?.focus(),
                      0
                    );
                  }}
                  onDelete={removeSession}
                  strings={widgetStrings}
                />
              )}

              {showPreChat ? (
                <PreChatForm
                  config={preChatForm}
                  onSubmit={handlePreChatSubmit}
                  strings={widgetStrings}
                />
              ) : (
                <>
                  <MainContent
                    view={ui.view}
                    viewMode={currentViewMode}
                    onModeChange={handleViewModeChange}
                    records={mergedRecords}
                    messages={displayMessages}
                    isTyping={chat.state.isTyping}
                    error={chat.state.error}
                    messagesEndRef={messagesEndRef}
                    messagesContainerRef={messagesContainerRef}
                    onMessagesScroll={handleMessagesScroll}
                    streamingMessage={chat.state.streamingMessage}
                    activeToolCalls={chat.state.activeToolCalls}
                    onButtonClick={handleButtonClick}
                    panelId={activePanelId}
                    labelledBy={showViewTabs ? activeTabId : titleId}
                    emptyStateMessage={welcomeMessage || undefined}
                    bubbleOptions={filteredBubbleOptions}
                    suggestedActions={suggestedActions}
                    onSuggestedAction={handleButtonClick}
                    onProductAction={handleProductAction}
                    onOrderAction={handleOrderAction}
                    onFormSubmit={handleFormSubmit}
                    showScrollToBottom={scrollToBottomVisible && ui.view === 'chat'}
                    newMessagesCount={newMessagesWhileScrolledUp}
                    onScrollToBottom={() => scrollToBottom('smooth')}
                    strings={widgetStrings}
                    direction={widgetDirection}
                  />

                  {telemetryEnabled && ui.view === 'chat' && (
                    <TelemetryPanel
                      data={telemetry.data}
                      loading={telemetry.loading}
                      error={telemetry.error}
                      showContext={telemetryShowContext}
                      onRefresh={telemetry.refresh}
                      strings={widgetStrings}
                    />
                  )}

                  {isOffline && offlineMessage && (
                    <div className="px-4 py-2 text-xs text-amber-500 bg-gray-50 border-t border-gray-200">
                      {offlineMessage}
                    </div>
                  )}

                  {consentRequired && (
                    <div className="px-4 py-2 text-xs text-gray-600 flex items-start gap-2 border-t border-gray-200">
                      <input
                        type="checkbox"
                        checked={hasConsent}
                        onChange={e => handleConsentToggle(e.target.checked)}
                        aria-label={widgetStrings.consentCheckboxLabel}
                        disabled={!domainAllowed}
                      />
                      <span>
                        {consentText}{' '}
                        {safeConsentLinkUrl && (
                          <a
                            href={safeConsentLinkUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-500 hover:underline"
                          >
                            {consentLinkText}
                          </a>
                        )}
                      </span>
                    </div>
                  )}

                  {statusMessage && (
                    <div className="px-4 pb-2 text-xs text-gray-500">{statusMessage}</div>
                  )}

                  <InputArea
                    input={chat.state.input}
                    setInput={(v: string) => chat.dispatch({ type: 'SET_INPUT', input: v })}
                    handleSend={handleSend}
                    loading={chat.state.loading}
                    attachments={ui.attachments}
                    attachmentErrors={ui.attachmentErrors}
                    allowAttachments={allowAttachments}
                    allowVoice={allowVoice}
                    allowVideo={allowVideo}
                    theme={theme}
                    fileInputRef={fileInputRef}
                    textAreaRef={textAreaRef}
                    onFilesSelected={handleFileUpload}
                    onRemoveAttachment={(id: string) =>
                      uiDispatch({ type: 'REMOVE_ATTACHMENT', id })
                    }
                    onVoiceClick={handleVoiceClick}
                    onVideoClick={handleVideoClick}
                    disabled={inputDisabled}
                    maxInputLength={maxInputLength}
                    inputWarningThreshold={inputWarningThreshold}
                    allowedFileTypes={allowedFileTypes}
                    strings={widgetStrings}
                    placeholder={placeholder || widgetStrings.messagePlaceholder}
                  />
                </>
              )}
            </div>
          )}

          {/* Accessibility live region */}
          <div className="sr-only" aria-live="polite">
            {chat.state.loading && widgetStrings.loading}
            {chat.state.error && `Error: ${chat.state.error}`}
            {chat.state.isTyping && `${widgetStrings.agentTypingAria}...`}
            {minimized &&
              ui.unreadCount > 0 &&
              formatWidgetString(widgetStrings.liveRegionUnread, {
                count: ui.unreadCount,
                plural: ui.unreadCount === 1 ? '' : 's',
              })}
          </div>
        </div>
      </ChatProvider>
    );
  }
);

const ChatWidget = React.forwardRef<ChatWidgetAPI, ChatWidgetProps>(
  function ChatWidget(props, ref) {
    return (
      <ErrorBoundary>
        <ChatWidgetInner {...props} ref={ref} />
      </ErrorBoundary>
    );
  }
);

ChatWidget.displayName = 'ChatWidget';

export default ChatWidget;
